import { useCallback, useEffect, useRef, useState } from "react";
import {
  type PttSidecarConfig,
  type PttSidecarProvider,
  SidecarError,
  speakText,
  transcribeAudio,
} from "./pttSidecarClient";

export type { PttSidecarConfig, PttSidecarProvider };
export { SidecarError };

export type PttSidecarStatus = "unknown" | "online" | "offline";

const HEALTH_POLL_MS = 10_000;

export function usePttSidecar(config: PttSidecarConfig | null) {
  const [status, setStatus] = useState<PttSidecarStatus>("unknown");
  const configRef = useRef(config);
  configRef.current = config;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const checkHealth = useCallback(async () => {
    const cfg = configRef.current;
    if (!cfg) {
      if (mountedRef.current) setStatus("offline");
      return;
    }
    try {
      const res = await fetch(`${cfg.baseUrl}/health`, {
        signal: AbortSignal.timeout(3000),
      });
      if (mountedRef.current) setStatus(res.ok ? "online" : "offline");
    } catch {
      if (mountedRef.current) setStatus("offline");
    }
  }, []);

  useEffect(() => {
    if (!config) return;
    void checkHealth();
    const id = setInterval(() => void checkHealth(), HEALTH_POLL_MS);
    return () => clearInterval(id);
  }, [config, checkHealth]);

  const transcribe = useCallback(async (blob: Blob): Promise<string> => {
    const cfg = configRef.current;
    if (!cfg) throw new SidecarError("PTT sidecar not configured");
    return transcribeAudio(cfg, blob);
  }, []);

  const speak = useCallback(async (text: string): Promise<void> => {
    const cfg = configRef.current;
    if (!cfg) return;
    await speakText(cfg, text);
  }, []);

  return { status, transcribe, speak };
}
